import React, { useMemo, useState } from "react";
import shuffle from "lodash/shuffle";
import Design from "./designs/Design";
import RoundedTabs from "../../../../../../components/ui/tabs/rounded-tabs/RoundedTabs";
import RemoveFromLibrary from "../../../../../../components/ui/add-to-library-button/RemoveFromLibrary";
import DeleteTemplateModal from "../../DeleteTemplateModal";
import {
  getCardsTemplates,
  HIDDEN_CARDS_TEMPLATE_STYLES,
} from "./cards-templates";
import {
  CardsMasonry,
  CardsMasonryContainer,
  OverviewCardsTemplatesContainer,
} from "./styles";
import { useCardsStore } from "../../../../../../stores/use-cards-store";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import {
  CARD_DESIGN,
  CARD_DESIGN_COLUMNS,
  CARD_TYPE,
  CARDS_PROP,
} from "../../../settings/features/cards/defaults";
import { DesignContainer } from "./designs/styles";
import { Column } from "../../styles";
import { cardsLibraryService } from "@Shared/Service/CardsLibraryService";
import { MACRO_KEY } from "../../../../../defaults";
import { useMigrationStore } from "../../../../../../stores/use-migration-store";
import { buildCardsData } from "../../../../editor/handlers/macros/cards-migration";

export const OverviewTemplates = () => {
  const { data, setData } = useCardsStore((state) => ({
    data: state.data,
    setData: state.setData,
  }));
  const {
    setMacro,
    setDrawers,
    styleguideSetup = {},
    colorPalette,
  } = useOpticsStore((state) => ({
    setMacro: state.setMacro,
    setDrawers: state.setDrawers,
    styleguideSetup: state.styleguideSetup,
    colorPalette: state.colorPalette,
  }));

  const templates = useMemo(
    () =>
      getCardsTemplates({
        cornerRadius: styleguideSetup.cornerRadius,
        selectedColors: colorPalette,
        isInOverview: true,
      }),
    [styleguideSetup, colorPalette]
  );

  const hasData = !!Object.keys(data).length;

  return (
    <OverviewCardsTemplatesContainer>
      {templates.slice(0, 6).map((template) => (
        <DesignContainer
          key={template.card.id}
          hasHover
          style={{ cursor: "pointer" }}
          borderRadius={styleguideSetup.cornerRadius}
          onClick={() => {
            setMacro({ key: MACRO_KEY.CARDS });
            if (hasData) {
              const existingCard = data[0];

              const newData = data.map((item) => {
                const newConfig = {
                  ...item.config,
                  [CARDS_PROP.DESIGN]: template.config.design,
                  [CARDS_PROP.TYPE]: template.config.type,
                  [CARDS_PROP.LAYOUT]: template.config.layout,
                };
                const newCard = {
                  ...item.card,
                };
                if (template.config.type !== existingCard.config.type) {
                  newCard[CARDS_PROP.IMAGE] = template.card.image;
                  newCard[CARDS_PROP.ICON] = template.card.icon;
                }
                return {
                  ...item,
                  config: newConfig,
                  card: newCard,
                };
              });
              setData(newData, true);
            } else {
              setData(template);
            }
            setDrawers({ templates: false, settings: true });
          }}
        >
          <Design config={template.config} data={template.card} />
        </DesignContainer>
      ))}
    </OverviewCardsTemplatesContainer>
  );
};

const FromLibrary = ({ cards }) => {
  const [shuffled, setShuffled] = useState([]);
  const [cardToDelete, setCardToDelete] = useState();

  const { data, setData, setCard } = useCardsStore((state) => ({
    data: state.data,
    setData: state.setData,
    setCard: state.setCard,
  }));

  const { isMigrationActive, oldMacroData } = useMigrationStore();

  const {
    setDrawers,
    hasStyleguidePermission,
    setLibraryCards,
    isDrawerModalOpen,
    setIsDrawerModalOpen,
  } = useOpticsStore((state) => ({
    setDrawers: state.setDrawers,
    hasStyleguidePermission: state.hasStyleguidePermission,
    setLibraryCards: state.setLibraryCards,
    isDrawerModalOpen: state.isDrawerModalOpen,
    setIsDrawerModalOpen: state.setIsDrawerModalOpen,
  }));

  const hasData = !!Object.keys(data).length;

  const [firstColumnTemplates, secondColumnTemplates] = useMemo(() => {
    const shuffledIds = shuffled?.length
      ? shuffled
      : shuffle(cards.map((data) => data.id));

    setShuffled(shuffledIds);

    const shuffledFiltered = shuffledIds.map((id) =>
      cards.find((item) => item.id === id)
    );
    return splitInTwoArrays(shuffledFiltered);
  }, [cards]);

  const removeCardFromLibrary = async (id) => {
    setShuffled([...shuffled.filter((cardId) => cardId !== id)]);
    setLibraryCards([...cards.filter((card) => card.id !== id)]);
    try {
      await cardsLibraryService.deleteCard(id);
    } catch (error) {
      console.log("Failed to remove card");
    }
  };

  const handleClick = (template) => {
    if (hasData) {
      const newData = data.map((item) => {
        const newConfig = {
          ...item.config,
          [CARDS_PROP.DESIGN]: template.config.design,
          [CARDS_PROP.TYPE]: template.config.type,
          [CARDS_PROP.LAYOUT]: template.config.layout,
        };
        const newCard = {
          ...item.card,
          ...(template.config.design === CARD_DESIGN.IMAGE_EIGHT && {
            [CARDS_PROP.IMAGE_SECOND]: template.card.imageSecond,
            [CARDS_PROP.ICON]: template.card.icon,
          }),
          [CARDS_PROP.IMAGE]: template.cardData.image,
        };
        if (template.config.type === "emoji") {
          newCard[CARDS_PROP.ICON] = template.cardData.icon;
        }
        return {
          ...item,
          config: newConfig,
          card: newCard,
        };
      });
      setData(newData, true);
    } else if (isMigrationActive && oldMacroData.macro === MACRO_KEY.CARDS) {
      const builtData = buildCardsData(
        template,
        JSON.parse(oldMacroData?.data?.content || "{}")
      );
      setData(builtData);
    } else {
      const { cardData, ...rest } = template;
      setData({ card: cardData, ...rest });
    }
    setCard(undefined);
    setDrawers({ templates: false, settings: true });
  };

  return (
    <>
      <CardsMasonryContainer>
        <CardsMasonry>
          {firstColumnTemplates.map((template) => (
            <DesignContainer
              key={template.id}
              hasHover
              style={{ cursor: "pointer" }}
              onClick={() => handleClick(template)}
            >
              <Design
                config={{ ...template.config, columns: 3 }}
                data={template.cardData}
              />
              {hasStyleguidePermission && (
                <RemoveFromLibrary
                  onClick={(e) => {
                    e.stopPropagation();

                    setIsDrawerModalOpen(true);
                    setCardToDelete(template.id);
                  }}
                />
              )}
            </DesignContainer>
          ))}
        </CardsMasonry>

        <CardsMasonry>
          {secondColumnTemplates.map((template) => (
            <DesignContainer
              key={template.id}
              hasHover
              style={{ cursor: "pointer" }}
              onClick={() => handleClick(template)}
            >
              <Design
                config={{ ...template.config, columns: 3 }}
                data={template.cardData}
              />
              {hasStyleguidePermission && (
                <RemoveFromLibrary
                  onClick={(e) => {
                    e.stopPropagation();

                    setIsDrawerModalOpen(true);
                    setCardToDelete(template.id);
                  }}
                />
              )}
            </DesignContainer>
          ))}
        </CardsMasonry>
      </CardsMasonryContainer>

      <DeleteTemplateModal
        isOpen={isDrawerModalOpen}
        onClose={() => setIsDrawerModalOpen(false)}
        onConfirm={() => removeCardFromLibrary(cardToDelete)}
        macroName="card"
      />
    </>
  );
};

const splitInTwoArrays = (array) => {
  const size = Math.ceil(array.length / 2);
  const firstArray = array.slice(0, size);
  const secondArray = array.slice(size);
  return [firstArray, secondArray];
};

const BuiltTemplates = ({ selectedTab }) => {
  const [shuffled, setShuffled] = useState([]);

  const { data, setData, setCard } = useCardsStore((state) => ({
    data: state.data,
    setData: state.setData,
    setCard: state.setCard,
  }));

  const { setDrawers, styleguideSetup, colorPalette } = useOpticsStore(
    (state) => ({
      setDrawers: state.setDrawers,
      styleguideSetup: state.styleguideSetup,
      colorPalette: state.colorPalette,
    })
  );

  const { isMigrationActive, oldMacroData } = useMigrationStore();

  const hasData = !!Object.keys(data).length;

  const [firstColumnTemplates, secondColumnTemplates] = useMemo(() => {
    const allTemplates = getCardsTemplates({
      cornerRadius: styleguideSetup.cornerRadius,
      selectedColors: colorPalette,
    });

    const shuffledIds = shuffled?.length
      ? shuffled
      : shuffle(allTemplates.map((data) => data.card.id));

    setShuffled(shuffledIds);

    const filtered = allTemplates
      .filter(
        (data) =>
          selectedTab === "allTemplates" || data.config.type === selectedTab
      )
      .filter(
        (data) => !HIDDEN_CARDS_TEMPLATE_STYLES.includes(data.config.design)
      );

    const shuffledFiltered = shuffledIds
      .map((id) => filtered.find((item) => item.card.id === id))
      .filter((data) => !!data);
    return splitInTwoArrays(shuffledFiltered);
  }, [styleguideSetup, colorPalette, selectedTab]);

  const handleClick = (template) => {
    if (hasData) {
      const existingCard = data[0];

      const newData = data.map((item) => {
        const newDesign = template.config.design;
        const availableColumns =
          CARD_DESIGN_COLUMNS[newDesign] || CARD_DESIGN_COLUMNS.DEFAULT;
        const currentColumns = item.config.columns;

        const validColumns = availableColumns.includes(currentColumns)
          ? currentColumns
          : availableColumns.reduce((prev, curr) =>
              Math.abs(curr - currentColumns) < Math.abs(prev - currentColumns)
                ? curr
                : prev
            );

        const newConfig = {
          ...item.config,
          [CARDS_PROP.DESIGN]: newDesign,
          [CARDS_PROP.TYPE]: template.config.type,
          [CARDS_PROP.LAYOUT]: template.config.layout,
          [CARDS_PROP.COLUMNS]: validColumns,
        };
        const newCard = {
          ...item.card,
          [CARDS_PROP.COLORS]: template.card.colors,
          ...(template.config.design === CARD_DESIGN.IMAGE_EIGHT && {
            [CARDS_PROP.IMAGE_SECOND]: template.card.imageSecond,
            [CARDS_PROP.ICON]: template.card.icon,
          }),
        };
        if (template.config.type !== existingCard.config.type) {
          newCard[CARDS_PROP.IMAGE] = template.card.image;
          newCard[CARDS_PROP.ICON] = template.card.icon;
        }
        if (template.config.type === CARD_TYPE.NUMBER) {
          newCard[CARDS_PROP.NUMBER] = template.card.number;
        }

        return {
          ...item,
          config: newConfig,
          card: newCard,
        };
      });

      setData(newData, true);
    } else if (isMigrationActive && oldMacroData.macro === MACRO_KEY.CARDS) {
      const builtData = buildCardsData(
        template,
        JSON.parse(oldMacroData?.data?.content || "{}")
      );
      setData(builtData);
    } else {
      setData(template);
    }
    setCard(undefined);
    setDrawers({ templates: false, settings: true });
  };

  return (
    <CardsMasonryContainer>
      <CardsMasonry>
        {firstColumnTemplates.map((template) => (
          <DesignContainer
            key={template.card.id}
            hasHover
            style={{ cursor: "pointer" }}
            borderRadius={styleguideSetup.cornerRadius}
            onClick={() => handleClick(template)}
          >
            <Design config={template.config} data={template.card} />
          </DesignContainer>
        ))}
      </CardsMasonry>

      <CardsMasonry>
        {secondColumnTemplates
          .sort((a, b) => a.card.id - b.card.id)
          .map((template) => (
            <DesignContainer
              key={template.card.id}
              hasHover
              style={{ cursor: "pointer" }}
              borderRadius={styleguideSetup.cornerRadius}
              onClick={() => handleClick(template)}
            >
              <Design config={template.config} data={template.card} />
            </DesignContainer>
          ))}
      </CardsMasonry>
    </CardsMasonryContainer>
  );
};

const Tabs = [
  {
    id: "fl",
    i18n: "from-library",
    label: "From library",
    Content: FromLibrary,
  },
  {
    id: "allTemplates",
    label: "All templates",
    i18n: "all-templates",
    Content: BuiltTemplates,
  },
  {
    id: "image",
    label: "Image",
    i18n: "image",
    Content: BuiltTemplates,
  },
  {
    id: "emoji",
    label: "Emoji",
    i18n: "emoji",
    Content: BuiltTemplates,
  },
  {
    id: "illustration",
    label: "Illustration",
    i18n: "illustration",
    Content: BuiltTemplates,
  },
  {
    id: "steps",
    label: "Steps",
    i18n: "steps",
    Content: BuiltTemplates,
  },
  {
    id: "number",
    label: "Numbers",
    i18n: "numbers",
    Content: BuiltTemplates,
  },
  {
    id: "text",
    label: "Text",
    i18n: "text",
    Content: BuiltTemplates,
  },
];

const CardsTemplates = () => {
  const { libraryCards, selectedTemplateTab, setSelectedTemplateTab } =
    useOpticsStore((state) => ({
      libraryCards: state.libraryCards,
      selectedTemplateTab: state.selectedTemplateTab,
      setSelectedTemplateTab: state.setSelectedTemplateTab,
    }));

  const selectedTab = selectedTemplateTab.cards;

  const { Content } = Tabs.find(({ id }) => id === selectedTab);

  return (
    <Column>
      <RoundedTabs
        tabs={Tabs.filter(({ id }) => libraryCards?.length || id !== "fl")}
        selectedTab={selectedTab}
        setSelectedTab={(value) =>
          setSelectedTemplateTab({ macro: "cards", tab: value })
        }
      />

      <Content cards={libraryCards} selectedTab={selectedTab} />
    </Column>
  );
};

export default CardsTemplates;
